import { HttpClient, HttpErrorResponse, HttpEvent, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { download, Download } from './api-helpers';
import { Saver, SAVER } from './api-saver.provider';

/**
 * RESTAPI connector, please use only this service to connect to endpoints,
 * if something is missing, feel free to expand this service
 */
@Injectable({
  providedIn: 'root'
})
export class ApiService {

  constructor(
    private http: HttpClient,
    @Inject(SAVER) private save: Saver
  ) {
  }

  /**
   * Send GET to endpoint
   *
   * @param path URI path
   * @param newParams Request params
   * @param newHeaders Http header
   */
  get<T>(
    path: string,
    params: HttpParams = new HttpParams(),
    newHeaders?: HttpHeaders
  ): Observable<T> {
    const headers = newHeaders || new HttpHeaders();
    return this.http.get<T>(path, { headers, params })
      .pipe(
        tap(res => this.logDebugger(res, path, params, headers)),
        catchError(this.handleError)
      )
  }

  /**
   * Send POST command to endpoint
   *
   * @param path URI path
   * @param params Request params
   * @param headers Http header
   */
  post<T>(
    path: string,
    payload: any = {},
    params: HttpParams = new HttpParams(),
    newHeaders?: HttpHeaders
  ): Observable<T> {
    const headers = newHeaders || new HttpHeaders();
    return this.http.post<T>(path, payload, { headers, params })
      .pipe(
        tap(res => this.logDebugger(res, path, params, headers)),
        catchError(err => this.handleError(err, path, params, headers))
      )
  }

  /**
   * Send PUT command to endpoint
   *
   * @param path URI path
   * @param params Request params
   * @param headers Http header
   */
  put<T>(
    path: string,
    payload: any = {},
    params: HttpParams = new HttpParams(),
    newHeaders?: HttpHeaders
  ): Observable<T> {
    const headers = newHeaders || new HttpHeaders();
    return this.http.put<T>(path, payload, { headers, params })
      .pipe(
        tap(res => this.logDebugger(res, path, params, headers)),
        catchError(err => this.handleError(err, path, params, headers))
      )
  }

  /**
   * Send PUT command to endpoint
   *
   * @param path URI path
   * @param params Request params
   * @param headers Http header
   */
  delete<T>(
    path: string,
    params: HttpParams = new HttpParams(),
    newHeaders?: HttpHeaders
  ): Observable<T> {
    const headers = newHeaders || new HttpHeaders();
    return this.http.delete<T>(path, { headers, params })
      .pipe(
        tap(res => this.logDebugger(res, path, params, headers)),
        catchError(err => this.handleError(err, path, params, headers))
      )
  }

  /**
   * Download file as blob from endpoint
   * REMEBER! To implement file saver you must import ApiModule
   * or provide your own file saver
   * @example
   *   @NgModule({
   *     ...
   *     providers: [
   *       {provide: SAVER, useFactory: getSaver}
   *     ]
   *   })
   *   export class AppModule { }
   *
   * @param path URI path
   * @param params Request params
   * @param headers Http header
   */
  fileDownload(
    path: string,
    filename: string,
    params: HttpParams = new HttpParams(),
    newHeaders?: HttpHeaders
  ): Observable<Download> {
    const headers = newHeaders || new HttpHeaders();
    return this.http.get(path, {
      headers,
      params,
      reportProgress: true,
      observe: 'events',
      responseType: 'blob'
    })
      .pipe(
        tap(res => this.logDebugger(res, { filename }, path, params, headers)),
        download(blob => this.save(blob, filename)),
        catchError(err => this.handleError(err, path, params, headers))
      )
  }

  /**
   * Upload files as form to endpoint
   *
   * @param path URI path
   * @param params Request params
   * @param headers Http header
   */
  fileUpload<T>(
    path: string,
    payload: File,
    params: HttpParams = new HttpParams(),
    newHeaders?: HttpHeaders
  ): Observable<HttpEvent<T>> {
    const headers = newHeaders ||
      new HttpHeaders()
    return this.http.post<T>(path, payload, {
      headers,
      params,
      observe: 'events',
      reportProgress: true,
    })
      .pipe(
        tap(res => this.logDebugger(res, path, params, headers)),
        catchError(err => this.handleError(err, path, params, headers))
      )
  }

  private logDebugger(res: any, ...args: any): void {
    if (!environment.production) {
      console.log('[DEV-DEBUGGER] API request: ', args, res);
    }
  }

  /**
   * Catch all http errors and do something with them
   *
   * @param err Http error response
   * @param args
   */
  private handleError(err: HttpErrorResponse, ...args: any): Observable<never> {
    if (!environment.production) {
      console.error('[DEV-DEBUGGER] API request error: ', err, ...args);
    }
    return throwError(err);
  }
}
